import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext'; 
import axios from 'axios';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Card,
  CardContent,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem
} from '@mui/material';
import { 
    PieChart as RechartsChart, 
    Pie as RechartsPie, 
    Cell, 
    ResponsiveContainer 
} from 'recharts';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ScienceIcon from '@mui/icons-material/Science';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, TextField, Tabs, Tab } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import Avatar from '@mui/material/Avatar';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Chip from '@mui/material/Chip';
import MemberCard from '../components/MemberCard';
import QuizIcon from '@mui/icons-material/Quiz';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import axiosInstance from '../utils/axiosConfig';  
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SchoolIcon from '@mui/icons-material/School';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

const formatDate = (dateString) => {
  if (!dateString) return 'No date available';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Invalid date';
  }
};

const DashboardPage = () => {
    const { user } = useAuth();
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const response = await axiosInstance.get('credits/available');
                console.log('Dashboard credits response:', response.data);
                setCredits(response.data.availableCredits);
            } catch (error) {
                console.error('Error fetching credits:', error);
                setCredits(0);
            } finally {
                setLoading(false);
            }
        };

        fetchCredits();
    }, []);

    // Render different dashboard based on user role
    const renderDashboard = () => {
        console.log('Rendering dashboard for role:', user?.role);
        
        if (!user) {
            return <CircularProgress />;
        }

        switch (user.role) {
            case 'admin':
            case 'corporate':
                return <TeamDashboard viewType="corporate" initialCredits={credits} />;
            case 'school_admin':
                return <TeamDashboard viewType="school" initialCredits={credits} />;
            case 'individual':
                return <IndividualDashboard />;
            default:
                return (
                    <Alert severity="error">
                        Invalid user role: {user.role}
                    </Alert>
                );
        }
    };

    if (loading) return <CircularProgress />;
    return renderDashboard();
};

// Individual Dashboard Component
const IndividualDashboard = () => {
    const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState({
        credits: 3,
        quizResults: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                
                const creditsResponse = await axiosInstance.get('credits/available');
                const credits = creditsResponse.data.availableCredits;
                
                try {
                    const resultsResponse = await axiosInstance.get('quiz/results');
                    const latestQuiz = resultsResponse.data.results[0];
                    
                    if (latestQuiz) {
                        // Process quiz results if they exist
                        const scores = {
                            visual: latestQuiz.visualScore,
                            auditory: latestQuiz.auditoryScore,
                            kinesthetic: latestQuiz.kinestheticScore
                        };

                        const total = scores.visual + scores.auditory + scores.kinesthetic;
                        const primaryStyle = Object.entries(scores)
                            .reduce((a, b) => a[1] > b[1] ? a : b)[0];

                        setDashboardData({
                            credits,
                            quizResults: {
                                hasResults: true,
                                primaryStyle,
                                scores: {
                                    visual: Math.round((scores.visual / total) * 100),
                                    auditory: Math.round((scores.auditory / total) * 100),
                                    kinesthetic: Math.round((scores.kinesthetic / total) * 100)
                                },
                                rawScores: scores
                            }
                        });
                    } else {
                        // No quiz results yet
                        setDashboardData({
                            credits,
                            quizResults: {
                                hasResults: false
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error fetching quiz results:', error);
                    setDashboardData({
                        credits,
                        quizResults: {
                            hasResults: false
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setError('Error loading dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const renderQuizResults = () => {
        if (!dashboardData?.quizResults?.hasResults) {
            return (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        You haven't taken the learning style quiz yet.
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary"
                        href="/quiz"
                        startIcon={<QuizIcon />}
                        sx={{ mt: 2 }}
                    >
                        Take Quiz Now
                    </Button>
                </Box>
            );
        }

        const { primaryStyle, scores } = dashboardData.quizResults;

        // Prepare data for pie chart
        const data = [
            { name: 'Visual', value: scores.visual },
            { name: 'Auditory', value: scores.auditory },
            { name: 'Kinesthetic', value: scores.kinesthetic }
        ];

        return (
            <>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                        Primary Learning Style: {primaryStyle.charAt(0).toUpperCase() + primaryStyle.slice(1)}
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {getStyleDescription(primaryStyle)}
                    </Typography>
                </Box>

                {/* Pie Chart */}
                <Box sx={{ height: 200, mb: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <RechartsChart>
                            <RechartsPie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={60}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, value }) => `${name} ${value}%`}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </RechartsPie>
                        </RechartsChart>
                    </ResponsiveContainer>
                </Box>

                {/* Progress Bars */}
                <Box sx={{ mb: 3 }}>
                    {Object.entries(scores).map(([style, score]) => (
                        <Box key={style} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {style.charAt(0).toUpperCase() + style.slice(1)}
                                </Typography>
                                <Typography variant="body2">
                                    {score}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={score}
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    bgcolor: 'rgba(0,0,0,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: getStyleColor(style)
                                    }
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                {/* Learning Strategies and Challenges */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Optimal Strategies
                        </Typography>
                        <List dense>
                            {getStrategies(primaryStyle).map((strategy, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        <ThumbUpIcon color="primary" sx={{ fontSize: 20 }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={strategy}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Areas to Keep in Mind
                        </Typography>
                        <List dense>
                            {getChallenges(primaryStyle).map((challenge, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        <ThumbDownIcon color="error" sx={{ fontSize: 20 }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={challenge}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                </Grid>
            </>
        );
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Welcome Banner with Credits */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    mb: 4, 
                    background: 'linear-gradient(120deg, #2196f3 0%, #1976d2 100%)',
                    color: 'white',
                    borderRadius: 2
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4">
                        Welcome, {user?.first_name || 'User'}!
                    </Typography>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 150
                        }}
                    >
                        <Typography variant="overline" sx={{ color: 'primary.main' }}>
                            Available Credits
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            {dashboardData.credits}
                        </Typography>
                    </Paper>
                </Box>
            </Paper>

            {/* Quiz Results Section */}
            <Grid container spacing={3}>
                {/* Learning Style Overview */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Your Learning Style
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            renderQuizResults()
                        )}
                    </Paper>
                </Grid>

                {/* Recommendations */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Learning Recommendations
                        </Typography>
                        {dashboardData ? (
                            <List>
                                {getLearningRecommendations(dashboardData.quizResults.primaryStyle).map((rec, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <LightbulbIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={rec} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Complete the quiz to get personalized recommendations
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

// Helper functions
const getStyleColor = (style) => {
    const colors = {
        visual: '#FF6B6B',
        auditory: '#4ECDC4',
        kinesthetic: '#45B7D1'
    };
    return colors[style] || '#666666';
};

const getStyleDescription = (style) => {
    const descriptions = {
        visual: 'You learn best through visual aids like graphs, charts, and images.',
        auditory: 'You learn best through listening and verbal communication.',
        kinesthetic: 'You learn best through hands-on experience and physical activity.'
    };
    return descriptions[style] || '';
};

const getLearningRecommendations = (style) => {
    const recommendations = {
        visual: [
            'Use mind maps and diagrams',
            'Watch educational videos',
            'Create visual summaries',
            'Use color coding in notes'
        ],
        auditory: [
            'Record and listen to lectures',
            'Participate in group discussions',
            'Use verbal repetition',
            'Explain concepts to others'
        ],
        kinesthetic: [
            'Take hands-on workshops',
            'Use physical movement while studying',
            'Create physical models',
            'Practice through role-playing'
        ]
    };
    return recommendations[style] || [];
};

const getStrategies = (style) => {
    const strategies = {
        visual: [
            "Use diagrams, charts, and mind maps",
            "Watch educational videos",
            "Create visual summaries",
            "Use color-coding in notes",
            "Draw concepts and ideas"
        ],
        auditory: [
            "Record and listen to lectures",
            "Participate in group discussions",
            "Use verbal repetition",
            "Listen to educational podcasts",
            "Explain concepts to others"
        ],
        kinesthetic: [
            "Take hands-on approaches",
            "Use physical movement while learning",
            "Create physical models",
            "Practice through real-world applications",
            "Take frequent active breaks"
        ]
    };
    return strategies[style] || strategies.visual;
};

const getChallenges = (style) => {
    const challenges = {
        visual: [
            "May struggle with verbal instructions",
            "Can miss details in lectures",
            "Might need visual aids",
            "Can be distracted by visual clutter",
            "May need quiet study environments"
        ],
        auditory: [
            "May struggle with written materials",
            "Can be distracted by noise",
            "Might need verbal explanations",
            "Can find visual presentations challenging",
            "May need quiet time to process"
        ],
        kinesthetic: [
            "May struggle with traditional lectures",
            "Can become restless in static environments",
            "Might need movement breaks",
            "Can find long reading sessions difficult",
            "May need hands-on experience"
        ]
    };
    return challenges[style] || challenges.visual;
};

// Rename current dashboard to TeamDashboard
const TeamDashboard = ({ viewType = 'corporate', initialCredits = 0 }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [teamMembers, setTeamMembers] = useState([]);
    const [recommendations, setRecommendations] = useState([
        {
            title: 'Incomplete Assessments',
            description: 'Loading team members...',
            action: 'Send Reminders',
            icon: <QuizIcon />,
            priority: 'high',
            onClick: () => {
                const incompleteIds = getIncompleteMemberIds();
                console.log('Sending reminders to:', incompleteIds);
                sendReminders(incompleteIds);
            }
        },
        {
            title: 'Team Learning Profile',
            description: 'View your team\'s learning style distribution and insights.',
            action: 'View Profile',
            icon: <TipsAndUpdatesIcon />,
            priority: 'medium',
            onClick: () => navigate('/team')
        },
        {
            title: 'Manage Credits',
            description: 'Purchase additional credits or view credit history.',
            action: 'Manage Credits',
            icon: <ShoppingCartIcon />,
            priority: 'normal',
            onClick: () => navigate('/credits')
        }
    ]);

    // Add useEffect for fetching team data
    useEffect(() => {
        fetchTeamData();
    }, []);

    // Update useEffect for recommendations
    useEffect(() => {
        if (teamMembers.length > 0) {
            const incompleteCount = getIncompleteMemberIds().length;
            setRecommendations(prev => prev.map(rec => {
                if (rec.title === 'Incomplete Assessments') {
                    return {
                        ...rec,
                        description: `${incompleteCount} team member${incompleteCount !== 1 ? 's' : ''} haven't completed their assessment yet.`
                    };
                }
                return rec;
            }));
        }
    }, [teamMembers]);

    const getIncompleteMemberIds = () => {
        return teamMembers
            .filter(member => !member.quizResults?.length && member.id !== user.id)
            .map(member => member.id);
    };

    const sendReminders = async (incompleteMemberIds) => {
        console.log('Attempting to send reminders to:', incompleteMemberIds);
        
        if (!incompleteMemberIds?.length) {
            enqueueSnackbar('No members to send reminders to', { 
                variant: 'info' 
            });
            return;
        }

        try {
            const response = await axiosInstance.post('/api/team/send-reminders', {
                memberIds: incompleteMemberIds,
                message: 'Please complete your learning style assessment',
                teamId: user.team_id
            });

            console.log('Reminder response:', response.data);

            if (response.data.success) {
                enqueueSnackbar(`Reminders sent to ${incompleteMemberIds.length} team member(s)`, {
                    variant: 'success'
                });
                
                setRecommendations(prev => prev.map(rec => {
                    if (rec.title === 'Incomplete Assessments') {
                        return {
                            ...rec,
                            description: `Reminders sent to ${incompleteMemberIds.length} member(s). Check back later.`,
                            priority: 'medium'
                        };
                    }
                    return rec;
                }));

                // Refresh team data after sending reminders
                await fetchTeamData();
            }
        } catch (error) {
            console.error('Error sending reminders:', error);
            enqueueSnackbar(
                error.response?.data?.message || 'Failed to send reminders. Please try again.',
                { variant: 'error' }
            );
        }
    };

    // State variables
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [credits, setCredits] = useState(initialCredits);
    const [creditMessage, setCreditMessage] = useState('');
    const [creditSeverity, setCreditSeverity] = useState('info');
    const [teamStats, setTeamStats] = useState({
        totalMembers: 0,
        withQuizResults: 0,
        distribution: {
            visual: { count: 0, percentage: 0 },
            auditory: { count: 0, percentage: 0 },
            kinesthetic: { count: 0, percentage: 0 }
        }
    });

    const { token } = useAuth();

    // Fetch team data
    const fetchTeamData = async () => {
        try {
            console.log('Fetching team data...');
            setLoading(true);
            
            const response = await axiosInstance.get('/api/team/members');
            console.log('Team data response:', response.data);
            
            if (response.data.success) {
                const members = response.data.members;
                console.log('Processing members:', members); // Debug log

                setTeamMembers(members);

                // Calculate team statistics
                const stats = {
                    totalMembers: members.length,
                    withQuizResults: members.filter(m => m.quizResults?.length > 0).length,
                    distribution: calculateDistribution(members)
                };
                
                setTeamStats(stats);

                // Update recommendations with actual incomplete count
                const incompleteCount = members.filter(m => !m.quizResults?.length && m.id !== user.id).length;
                console.log('Incomplete members count:', incompleteCount); // Debug log

                setRecommendations(prev => prev.map(rec => {
                    if (rec.title === 'Incomplete Assessments') {
                        return {
                            ...rec,
                            description: `${incompleteCount} team member${incompleteCount !== 1 ? 's' : ''} haven't completed their assessment yet.`,
                            onClick: () => {
                                const incompleteIds = members
                                    .filter(m => !m.quizResults?.length && m.id !== user.id)
                                    .map(m => m.id);
                                console.log('Incomplete member IDs:', incompleteIds); // Debug log
                                if (incompleteIds.length > 0) {
                                    sendReminders(incompleteIds);
                                } else {
                                    enqueueSnackbar('All team members have completed their assessments', { 
                                        variant: 'info' 
                                    });
                                }
                            }
                        };
                    }
                    return rec;
                }));
            }
        } catch (error) {
            console.error('Error fetching team data:', error);
            setError('Failed to load team data. Please try again later.');
            setTeamMembers([]);
            setTeamStats({
                totalMembers: 0,
                withQuizResults: 0,
                distribution: {
                    visual: { count: 0, percentage: 0 },
                    auditory: { count: 0, percentage: 0 },
                    kinesthetic: { count: 0, percentage: 0 }
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to calculate distribution
    const calculateDistribution = (members) => {
        const distribution = {
            visual: { count: 0, percentage: 0 },
            auditory: { count: 0, percentage: 0 },
            kinesthetic: { count: 0, percentage: 0 }
        };

        const membersWithQuizzes = members.filter(member => 
            member.quizResults && member.quizResults.length > 0
        );

        membersWithQuizzes.forEach(member => {
            const result = member.quizResults[0];
            const scores = {
                visual: result.visual_score,
                auditory: result.auditory_score,
                kinesthetic: result.kinesthetic_score
            };

            const maxScore = Math.max(...Object.values(scores));
            const dominantStyle = Object.entries(scores)
                .find(([_, score]) => score === maxScore)?.[0];

            if (dominantStyle) {
                distribution[dominantStyle].count++;
            }
        });

        // Calculate percentages
        const total = membersWithQuizzes.length;
        if (total > 0) {
            Object.keys(distribution).forEach(style => {
                distribution[style].percentage = 
                    Math.round((distribution[style].count / total) * 100);
            });
        }

        return distribution;
    };

    // Fetch credits
    const fetchCredits = async () => {
        try {
            const response = await axiosInstance.get('credits/available');
            console.log('Team credits response:', response.data);
            
            if (response.data.success) {
                setCredits(response.data.availableCredits);
                // Show credit source in message
                const source = response.data.creditSource;
                setCreditMessage(
                    source === 'team' ? 'Using team credit pool' :
                    source === 'school' ? 'Using school credit pool' :
                    'Using personal credits'
                );
            }
        } catch (error) {
            console.error('Error fetching credits:', error);
            setCreditMessage('Error fetching credit balance');
            setCreditSeverity('error');
            setCredits(initialCredits);
        }
    };

    // Generate recommendations based on team data
    const generateRecommendations = (members) => {
        let updatedRecs = [...recommendations]; // Start with existing recommendations

        // Update incomplete assessments recommendation if needed
        const incompleteMembersCount = getIncompleteMemberIds().length;
        if (incompleteMembersCount > 0) {
            updatedRecs = updatedRecs.map(rec => {
                if (rec.title === 'Incomplete Assessments') {
                    return {
                        ...rec,
                        description: `${incompleteMembersCount} team member${
                            incompleteMembersCount > 1 ? 's' : ''
                        } haven't completed their assessment yet.`,
                        priority: 'high'
                    };
                }
                return rec;
            });
        }

        // Update team learning profile description if needed
        const dominantStyle = Object.entries(teamStats.distribution)
            .reduce((a, b) => a[1].count > b[1].count ? a : b)[0];
        
        updatedRecs = updatedRecs.map(rec => {
            if (rec.title === 'Team Learning Profile') {
                return {
                    ...rec,
                    description: `Your team shows a ${dominantStyle} learning preference. View detailed insights and recommendations.`,
                    priority: 'medium'
                };
            }
            return rec;
        });

        setRecommendations(updatedRecs);
    };

    useEffect(() => {
        fetchTeamData();
        fetchCredits();
    }, []);

    useEffect(() => {
        if (teamMembers.length > 0) {
            generateRecommendations(teamMembers);
        }
    }, [teamMembers, teamStats, credits]);

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    // Content variations based on viewType
    const content = {
        corporate: {
            welcomeText: 'Managing team members',
            memberLabel: 'team member',
            groupLabel: 'Team Groups',
            recommendations: [
                {
                    title: 'Incomplete Assessments',
                    description: 'Some team members haven\'t completed their assessment yet.',
                    action: 'Send Reminders',
                    icon: <QuizIcon />,
                    priority: 'high',
                    onClick: () => {
                        const incompleteIds = getIncompleteMemberIds();
                        console.log('Sending reminders to:', incompleteIds); // Debug log
                        sendReminders(incompleteIds);
                    }
                },
                {
                    title: 'Team Learning Profile',
                    description: 'View your team\'s learning style distribution and insights.',
                    action: 'View Profile',
                    icon: <TipsAndUpdatesIcon />,
                    priority: 'medium',
                    onClick: () => navigate('/team')
                },
                // ... other corporate recommendations
            ]
        },
        school: {
            welcomeText: 'Managing students',
            memberLabel: 'student',
            groupLabel: 'Classes',
            recommendations: [
                {
                    title: 'Pending Assessments',
                    description: 'Some students haven\'t completed their assessment yet.',
                    action: 'Send Reminders',
                    icon: <QuizIcon />,
                    priority: 'high',
                    onClick: () => sendReminders(getIncompleteMemberIds())
                },
                {
                    title: 'Class Learning Profiles',
                    description: 'View your students\' learning style distribution and insights.',
                    action: 'View Profiles',
                    icon: <TipsAndUpdatesIcon />,
                    priority: 'medium',
                    onClick: () => navigate('/classes')
                },
                {
                    title: 'Manage Classes',
                    description: 'Create and manage class groups for better organization.',
                    action: 'Manage Classes',
                    icon: <GroupIcon />,
                    priority: 'normal',
                    onClick: () => navigate('/classes/manage')
                },
                {
                    title: 'Learning Resources',
                    description: 'Access educational materials and teaching guides.',
                    action: 'View Resources',
                    icon: <SchoolIcon />,
                    priority: 'normal',
                    onClick: () => navigate('/resources')
                }
            ]
        }
    }[viewType];

    // Modify the return JSX to use dynamic content
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Welcome Banner with Credits */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: { xs: 3, md: 3 }, 
                    mb: { xs: 2, md: 4 }, 
                    background: 'linear-gradient(120deg, #2196f3 0%, #1976d2 100%)',
                    color: 'white',
                    borderRadius: 2,
                    width: '100%'
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                mb: { xs: 1, md: 2 }
                            }}
                        >
                            Welcome back, {user?.first_name}!
                        </Typography>
                        <Typography 
                            variant="subtitle1"
                            sx={{ 
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                opacity: 0.9 
                            }}
                        >
                            Managing {teamStats.totalMembers} team members
                        </Typography>
                    </Box>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 150
                        }}
                    >
                        <Typography variant="overline" sx={{ color: 'primary.main' }}>
                            Available Credits
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            {credits}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            1 credit per assessment
                        </Typography>
                    </Paper>
                </Box>
                
                {creditMessage && (
                    <Alert 
                        severity={creditSeverity}
                        sx={{ mt: 2 }}
                        onClose={() => setCreditMessage('')}
                    >
                        {creditMessage}
                    </Alert>
                )}
            </Paper>

            {/* Key Metrics */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        sx={{
                            p: { xs: 3, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: 'auto',
                            minHeight: { xs: 120, md: 140 },
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                            Total Members
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
                            {teamStats.totalMembers}
                        </Typography>
                        <GroupIcon color="primary" />
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        sx={{
                            p: { xs: 3, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: 'auto',
                            minHeight: { xs: 120, md: 140 },
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                            Quiz Completion Rate
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
                            {Math.round((teamStats.withQuizResults / teamStats.totalMembers) * 100)}%
                        </Typography>
                        <QuizIcon color="primary" />
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        sx={{
                            p: { xs: 3, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: 'auto',
                            minHeight: { xs: 120, md: 140 },
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                            Available Credits
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
                            {credits}
                        </Typography>
                        <TimerIcon color="primary" />
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        sx={{
                            p: { xs: 3, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            height: 'auto',
                            minHeight: { xs: 120, md: 140 },
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                            Dominant Style
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
                            {Object.entries(teamStats.distribution)
                                .reduce((a, b) => a[1].count > b[1].count ? a : b)[0]
                                .charAt(0).toUpperCase() + 
                                Object.entries(teamStats.distribution)
                                .reduce((a, b) => a[1].count > b[1].count ? a : b)[0]
                                .slice(1)
                            }
                        </Typography>
                        <LightbulbIcon color="primary" />
                    </Paper>
                </Grid>
            </Grid>

            {/* Learning Styles Distribution */}
            <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                        p: { xs: 3, md: 3 },
                        height: '100%',
                        minHeight: { xs: 'auto', md: 400 }
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Learning Styles Distribution
                        </Typography>
                        {Object.entries(teamStats.distribution).map(([style, data]) => (
                            <Box key={style} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">
                                        {style.charAt(0).toUpperCase() + style.slice(1)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {data.percentage}% ({data.count} members)
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={data.percentage}
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        bgcolor: 'rgba(0,0,0,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: style === 'visual' ? '#FF6B6B' :
                                                    style === 'auditory' ? '#4ECDC4' : '#45B7D1'
                                        }
                                    }}
                                />
                            </Box>
                        ))}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                        p: { xs: 3, md: 3 },
                        height: '100%',
                        minHeight: { xs: 'auto', md: 400 }
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Recent Team Activity
                        </Typography>
                        <List>
                            {teamMembers.slice(0, 5).map((member, index) => (
                                <ListItem key={index} divider={index < 4}>
                                    <ListItemIcon>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            {member.first_name?.[0]}
                                        </Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${member.first_name} ${member.last_name}`}
                                        secondary={member.quizResults?.length > 0 ? 
                                            `Completed quiz on ${formatDate(member.quizResults[0].created_at)}` :
                                            'No quiz taken yet'
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>

            {/* Action Center */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: { xs: 2, md: 4 } }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: { xs: 3, md: 3 } }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                            Action Center
                        </Typography>
                        
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            {recommendations.map((rec, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Paper 
                                        elevation={3}
                                        sx={{ 
                                            p: 3,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            bgcolor: rec.priority === 'high' ? 'error.light' : 
                                                    rec.priority === 'medium' ? 'warning.light' : 
                                                    'background.paper'
                                        }}
                                    >
                                        <Box sx={{ position: 'absolute', right: 16, top: 16 }}>
                                            <Chip 
                                                label={rec.priority}
                                                size="small"
                                                color={rec.priority === 'high' ? 'error' : 
                                                       rec.priority === 'medium' ? 'warning' : 
                                                       'info'}
                                            />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            {rec.icon}
                                            <Typography variant="h6" sx={{ ml: 1 }}>
                                                {rec.title}
                                            </Typography>
                                        </Box>
                                        
                                        <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                                            {rec.description}
                                        </Typography>
                                        
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            fullWidth
                                            sx={{ mt: 'auto' }}
                                            startIcon={rec.icon}
                                            onClick={() => {
                                                console.log('Button clicked for:', rec.title); // Debug log
                                                rec.onClick();
                                            }}
                                            color={rec.priority === 'high' ? 'error' : 
                                                   rec.priority === 'medium' ? 'warning' : 
                                                   'primary'}
                                        >
                                            {rec.action}
                                        </Button>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default DashboardPage;